import { useEffect, useRef } from 'react';
import { loadDocsApi } from './loadDocsApi';

let instanceCounter = 0;

/**
 * Mounts an ONLYOFFICE editor for a signed config into a placeholder element.
 * DocsAPI replaces the placeholder with its own iframe. The editor is torn down
 * on unmount and re-created only when the document key changes (a new
 * session/version), so re-renders don't rebuild the whole editor.
 */
export default function OnlyOfficeEditor({
  documentServerUrl,
  config,
  onStateChange,
  onError,
  onReady,
}) {
  const idRef = useRef(null);
  if (!idRef.current) idRef.current = `onlyoffice-editor-${(instanceCounter += 1)}`;
  const editorRef = useRef(null);
  const cbRef = useRef({ onStateChange, onError, onReady });
  cbRef.current = { onStateChange, onError, onReady };

  const documentKey = config?.document?.key;

  useEffect(() => {
    if (!config) return undefined;
    let cancelled = false;

    loadDocsApi(documentServerUrl)
      .then((DocsAPI) => {
        if (cancelled) return;
        editorRef.current = new DocsAPI.DocEditor(idRef.current, {
          ...config,
          width: '100%',
          height: '100%',
          events: {
            // data === true means there are unsaved changes in the editor.
            onDocumentStateChange: (e) => cbRef.current.onStateChange?.(Boolean(e?.data)),
            onError: (e) => cbRef.current.onError?.(e?.data ?? 'Editor error'),
            onAppReady: () => cbRef.current.onReady?.(),
          },
        });
      })
      .catch((err) => {
        if (!cancelled) cbRef.current.onError?.(err?.message || 'Failed to load editor');
      });

    return () => {
      cancelled = true;
      try {
        editorRef.current?.destroyEditor?.();
      } catch {
        /* editor already gone */
      }
      editorRef.current = null;
    };
    // Rebuild only on a new document/server; callbacks are read via cbRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentKey, documentServerUrl]);

  return <div id={idRef.current} className="h-full w-full" />;
}
